import lodashCloneDeep from 'lodash/cloneDeep'
import { isRef, toRaw } from 'vue'

export const exhaustiveCheck = (_param: never): never => {
  throw new Error('exhaustive check')
}

/**
 * Deep-clone a value, with the semantics of lodash `cloneDeep` used in the
 * Vue 2 version: Date/Map/Set/RegExp/typed arrays are copied, class instances
 * keep their prototype, and values that cannot be cloned (File, Blob,
 * functions…) are kept by reference instead of being lost (an unsaved ad
 * keeps its video File) or throwing (structuredClone).
 *
 * Vue reactive proxies / refs are unwrapped first (via `toRaw`) so the copy
 * holds no proxy.
 */
export function cloneDeep<T>(value: T): T {
  return lodashCloneDeep(toRawDeep(value))
}

function isPlainObject(value: object): value is Record<string, unknown> {
  const proto: unknown = Object.getPrototypeOf(value)
  return proto === Object.prototype || proto == null
}

/** Unwraps refs and reactive proxies, recursing only into plain objects and
 * arrays: any other object (File, Date, Map, class instance…) is returned
 * as is (raw). */
function toRawDeep<T>(value: T): T {
  if (value == null || typeof value !== 'object') return value
  if (isRef(value)) return toRawDeep(value.value as T)
  const raw = toRaw(value)
  if (Array.isArray(raw)) {
    return raw.map((v: unknown) => toRawDeep(v)) as T
  }
  if (!isPlainObject(raw)) return raw
  const out: Record<string, unknown> = {}
  for (const k of Object.keys(raw)) {
    out[k] = toRawDeep(raw[k])
  }
  return out as T
}

export type TranslateString = string

/**
 * Same as Object.keys() but with better typing
 * */
export function keys<T extends object>(o: T): (keyof T)[] {
  return Object.keys(o) as (keyof T)[]
}

/**
 * Not nullish typeguard
 * */
export function isNotNullish<T>(value: T | null | undefined): value is T {
  return value != null
}

type ObjectEntry<BaseType> = [keyof BaseType, BaseType[keyof BaseType]]
/**
 * Same as Object.entries() but with better typing
 * */
export function entries<T extends object>(obj: T): ObjectEntry<T>[] {
  return Object.entries(obj) as ObjectEntry<T>[]
}

/**
 * Same as Object.fromEntries() but with better typing
 * */
export function fromEntries<K extends string, T>(
  entries: Iterable<readonly [K, T]>,
): { [k in K]: T } {
  return Object.fromEntries(entries) as { [k in K]: T }
}

/**
 * Map an object by its entries (tuples key/value)
 * */
export function mapByEntries<T extends object, MappedK extends string, MappedV>(
  obj: T,
  mapper: (entry: ObjectEntry<T>) => [MappedK, MappedV],
): { [k in MappedK]: MappedV } {
  return fromEntries(entries(obj).map(mapper))
}

// https://github.com/piotrwitek/utility-types/blob/411e83ecf70e428b529fc2a09a49519e8f36c8fa/src/mapped-types.ts#L616
/**
 * @desc From `T` make a set of properties by key `K` become required
 * @example
 * ```ts
 *    type Props = {
 *      name?: string;
 *      age?: number;
 *      visible?: boolean;
 *    };
 *
 *    // Expect: { name: string; age: number; visible: boolean; }
 *    type Props = AugmentedRequired<Props>;
 *
 *    // Expect: { name?: string; age: number; visible: boolean; }
 *    type Props = AugmentedRequired<Props, 'age' | 'visible'>;
 * ```
 */
export type AugmentedRequired<
  T extends object,
  K extends keyof T = keyof T,
> = Omit<T, K> & Required<Pick<T, K>>

// https://github.com/piotrwitek/utility-types/blob/411e83ecf70e428b529fc2a09a49519e8f36c8fa/src/mapped-types.ts#L556
/**
 * Optional
 * @desc From `T` make a set of properties by key `K` become optional
 * @example
 *    type Props = {
 *      name: string;
 *      age: number;
 *      visible: boolean;
 *    };
 *
 *    // Expect: { name?: string; age?: number; visible?: boolean; }
 *    type Props = Optional<Props>;
 *
 *    // Expect: { name: string; age?: number; visible?: boolean; }
 *    type Props = Optional<Props, 'age' | 'visible'>;
 */
export type Optional<T extends object, K extends keyof T = keyof T> = Omit<
  T,
  K
> &
  Partial<Pick<T, K>>

/**
 * Determines whether a given color is dark or light.
 * @param color The color in hex format.
 */
export function isColorDark(color: string): 'light' | 'dark' {
  const _color = color.startsWith('#') ? color.substring(1, 7) : color
  const r = parseInt(_color.substring(0, 2), 16) // hexToR
  const g = parseInt(_color.substring(2, 4), 16) // hexToG
  const b = parseInt(_color.substring(4, 6), 16) // hexToB
  return r * 0.299 + g * 0.587 + b * 0.114 > 186 ? 'light' : 'dark'
}

export function localeToFlag(locale: string): string {
  if (locale === 'en') {
    // map 'en' to 'uk' flag
    return '🇬🇧'
  }
  if (locale.length === 2) {
    return String.fromCodePoint(
      ...[...locale.toUpperCase()].map((char) => char.charCodeAt(0) + 127397),
    )
  }
  return locale
}

/**
 * Escapes a string so it is safe to embed inside an HTML attribute value
 * delimited by single quotes.
 */
export function escapeHtmlAttribute(value: string): string {
  return value.replace(
    /['&<>]/g,
    // /['"&<>]/g,
    (char) =>
      ({
        "'": '&#39;',
        // '"': '&quot;',
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
      })[char] ?? '',
  )
}
